import { Fragment, type ReactNode } from "react";

/**
 * Minimal, safe markdown for AI output: headings, bullets, **bold**, _italic_, paragraphs.
 * Builds React elements directly - never injects HTML - so model output cannot run script.
 */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|_[^_]+_|\*[^*\s][^*]*\*)/g;
  const word = /\w/;
  let last = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    const t = m[0];
    // _x_ only counts as italics at word edges, so identifiers like obesity_class_2 stay intact.
    // (Checked by hand: regex lookbehind is not available in older iOS Safari.)
    if (t.startsWith("_") && (word.test(text[m.index - 1] ?? "") || word.test(text[m.index + t.length] ?? ""))) {
      re.lastIndex = m.index + 1;
      continue;
    }
    if (m.index > last) out.push(text.slice(last, m.index));
    if (t.startsWith("**")) out.push(<strong key={k++}>{t.slice(2, -2)}</strong>);
    else out.push(<em key={k++}>{t.slice(1, -1)}</em>);
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export default function Markdown({ text }: { text: string }) {
  const blocks: ReactNode[] = [];
  let list: string[] = [];
  const flush = () => {
    if (list.length) {
      blocks.push(
        <ul key={blocks.length}>
          {list.map((li, i) => (
            <li key={i}>{inline(li)}</li>
          ))}
        </ul>,
      );
      list = [];
    }
  };
  for (const raw of text.split("\n")) {
    const line = raw.trimEnd();
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line) ?? /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (bullet) {
      list.push(bullet[1]);
      continue;
    }
    flush();
    if (!line.trim()) continue;
    const h = /^#{1,6}\s+(.*)$/.exec(line);
    if (h) blocks.push(<h4 key={blocks.length}>{inline(h[1])}</h4>);
    else blocks.push(<p key={blocks.length}>{inline(line)}</p>);
  }
  flush();
  return <Fragment>{blocks}</Fragment>;
}
